import { inject } from 'inversify';

import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { SignalrListener, SignalrMessage } from '@/decorators/signalr';
import { TripNote } from '@/entities/TripNote';
import CONSTANTS from '@/helpers/constants';
import { Logger } from '@/services/Logger';
import { TripNoteUpdateReqMsg, TripNotesReqMsg } from '@/services/signalr/MessageTypes/TripNote';
import { Signalr } from '@/services/signalr/Signalr';
import { TripNoteStore } from '@/stores/TripNoteStore';
import { ITripNote } from '~/api/ITripNote';
import { IDispatchMessage } from '~/api/messages/IDispatchMessage';
import { ITripNotesRespMsg } from '~/api/messages/ITripNotesRespMsg';

@SignalrListener
@service(TripNotesManager)
export class TripNotesManager {
  @inject(Logger) protected logger: Logger;
  @inject(Signalr) protected signalr: Signalr;
  @inject(TripNoteStore) protected tripNoteStore: TripNoteStore;
  @inject(UserController) protected userController: UserController;

  getActiveTripNotes(tripId: string) {
    return this.tripNoteStore.all().filter((note) => note.tripId === tripId);
  }

  @SignalrMessage('TripNotesRespMsg')
  async onTripNotesRespMsg(notes: ITripNotesRespMsg & IDispatchMessage) {
    this.tripNoteStore.batchUpdate(notes.tripNotes);
  }

  async update(tripNote: ITripNote, context: string = CONSTANTS.UI.CONTEXTS.TRIP_NOTES) {
    const { fullName } = this.userController.activeUser;
    const normalized = new TripNote({ ...tripNote, who: fullName });
    const tripNotes = [normalized];
    if (tripNote.tripId) {
      await this.signalr.invoke(TripNoteUpdateReqMsg, {
        tripNotes, contextKey: context,
      });
    }
    else {
      this.updateTmpTripNote(normalized);
    }
  }

  async load(tripId: string) {
    await this.signalr.invoke(TripNotesReqMsg, { tripId });
  }

  getAllTmpTripNotes() {
    return this.tripNoteStore.tmpTripNotes;
  }

  addTmpTripNote(note: ITripNote) {
    const { fullName } = this.userController.activeUser;
    const normalized = new TripNote({ ...note, who: fullName });
    this.tripNoteStore.updateTmpNote(...[normalized]);
  }

  updateTmpTripNote(note: ITripNote) {
    this.tripNoteStore.updateTmpNote(...[note]);
  }

  deleteTmpTripNote(noteId: number) {
    this.tripNoteStore.deleteTmpNote(noteId);
  }

  clearTmpTripNotes() {
    this.tripNoteStore.tmpTripNotes = [];
  }
}
